import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Heart, Trash2, X } from "lucide-react";
import { useWishlist } from "@/contexts/WishlistContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { Link } from "react-router-dom";

interface WishlistDrawerProps {
  children: React.ReactNode;
}

const WishlistDrawer = ({ children }: WishlistDrawerProps) => {
  const { items, removeFromWishlist, totalItems, clearWishlist } = useWishlist();
  const { formatPrice } = useCurrency();

  return (
    <Sheet>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className="w-full sm:max-w-md bg-background border-border flex flex-col p-4 sm:p-6">
        <SheetHeader className="border-b border-border pb-4 flex-shrink-0">
          <SheetTitle className="font-display text-xl text-foreground flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary fill-primary" />
            Wishlist ({totalItems})
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col flex-1 min-h-0 mt-4">
          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <Heart className="w-16 h-16 text-muted-foreground mb-4" />
              <p className="font-subtitle text-muted-foreground">Your wishlist is empty</p>
              <p className="font-subtitle text-sm text-muted-foreground mt-1">
                Save items you love for later
              </p>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                {items.map((item) => (
                  <Link
                    key={item.id}
                    to={`/product/${item.handle}`}
                    className="flex gap-4 p-3 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-colors"
                  >
                    <div className="w-20 h-20 rounded-lg bg-secondary/50 overflow-hidden flex-shrink-0">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-xs text-muted-foreground">No Image</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {item.productType && (
                        <p className="text-xs font-subtitle text-primary uppercase tracking-wider">
                          {item.productType}
                        </p>
                      )}
                      <h4 className="font-display font-medium text-foreground truncate">
                        {item.title}
                      </h4>
                      <p className="font-subtitle font-semibold text-foreground mt-1">
                        {formatPrice(item.price)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeFromWishlist(item.id);
                      }}
                      aria-label="Remove from wishlist"
                      className="h-9 w-9 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive transition-colors flex-shrink-0 self-start"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Link>
                ))}
              </div>

              <div className="border-t border-border pt-4 mt-4 flex-shrink-0 safe-bottom">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-muted-foreground"
                  onClick={clearWishlist}
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Wishlist
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default WishlistDrawer;
